
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CuisineType, FoodType } from "@/types/recipe";

interface UseLibraryCountParams {
  search: string;
  selectedCreatorType: string | null;
  selectedNationality: string | null;
  selectedCuisineType: CuisineType | null;
  selectedFoodType: FoodType | null;
  viewMode: "recipes" | "creators";
}

export function useLibraryCount({
  search,
  selectedCreatorType,
  selectedNationality,
  selectedCuisineType,
  selectedFoodType,
  viewMode,
}: UseLibraryCountParams) {
  const { data: totalCount, isLoading } = useQuery({
    queryKey: ['library-count', search, selectedCreatorType, selectedNationality, selectedCuisineType, selectedFoodType, viewMode],
    queryFn: async () => {
      if (viewMode === "creators") {
        let query = supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true });

        if (search) {
          query = query.ilike('username', `%${search}%`);
        }

        if (selectedCreatorType) {
          query = query
            .not('creator_type', 'is', null)
            .contains('creator_type', JSON.stringify([selectedCreatorType]));
        }

        if (selectedNationality) {
          query = query.eq('nationality', selectedNationality);
        }

        const { count, error } = await query;
        if (error) throw error;
        return count || 0;
      } else {
        let query = supabase
          .from('recipes')
          .select(`
            id,
            user:profiles!inner (
              creator_type,
              nationality
            )
          `, { 
            count: 'exact',
            head: true 
          })
          .eq('visibility', 'public');

        if (search) {
          query = query.ilike('title', `%${search}%`);
        }

        if (selectedCreatorType) {
          query = query
            .not('profiles.creator_type', 'is', null)
            .contains('profiles.creator_type', JSON.stringify([selectedCreatorType]));
        }

        if (selectedNationality) {
          query = query.eq('profiles.nationality', selectedNationality);
        }

        if (selectedCuisineType) {
          query = query.eq('cuisine_type', selectedCuisineType);
        }

        if (selectedFoodType) {
          query = query.eq('food_type', selectedFoodType);
        }

        const { count, error } = await query;
        if (error) throw error;
        return count || 0;
      }
    },
  });

  return { totalCount, isLoading };
}
